import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Grid,
  Typography, Chip, Divider, Box, Slide, Switch, FormControlLabel
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { putData } from "../../BaseUrl";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Field = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || "-"}</Typography>
  </Box>
);

const SKILL_COLORS = ["#244C77", "#4caf50", "#ff9800", "#2196f3", "#336599", "#f44336"];

const DEFAULT_SKILLS = ["Art & Craft", "Cultural Activity"];

const VolunteerDetailDialog = ({ open, onClose, volunteer, onStatusToggle }) => {
  const [active, setActive] = useState(
    volunteer ? volunteer.isActive !== false : true
  );
  const [toggling, setToggling] = useState(false);

  // Sync local state when volunteer changes
  React.useEffect(() => {
    if (volunteer) setActive(volunteer.isActive !== false);
  }, [volunteer]);

  if (!volunteer) return null;

  const skills = volunteer.skills || volunteer.skillsExpertise || DEFAULT_SKILLS;
  const skillsList = typeof skills === "string"
    ? skills.split(",").map(s => s.trim()).filter(Boolean)
    : Array.isArray(skills) && skills.length > 0 ? skills : DEFAULT_SKILLS;

  const handleToggleStatus = async (e) => {
    const newActive = e.target.checked;
    setToggling(true);
    try {
      await putData(`admin/volunteer-admin/${volunteer.id}/status`, { isActive: newActive });
      setActive(newActive);
      if (onStatusToggle) onStatusToggle(volunteer.id, newActive);
    } catch (err) {
      // Revert on failure
      setActive(!newActive);
    }
    setToggling(false);
  };

  const statusCfg = {
    approved: { bg: "#e8f5e9", color: "#4caf50" },
    rejected: { bg: "#fce4ec", color: "#f44336" },
    pending:  { bg: "#fff3e0", color: "#ff9800" },
    "un-assigned": { bg: "#fce4ec", color: "#e91e63" }
  };
  const st = volunteer.status || "pending";
  const cfg = statusCfg[st] || statusCfg.pending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Transition}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {volunteer.name || `${volunteer.firstName || ""} ${volunteer.lastName || ""}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Volunteer #{volunteer.volunteerId || volunteer.id}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Contact Header */}
        <Box sx={{
          p: 2, mb: 3, borderRadius: 2, backgroundColor: "#f5f3ff",
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {volunteer.name || `${volunteer.firstName || ""} ${volunteer.lastName || ""}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">{volunteer.email || "-"}</Typography>
            <Typography variant="body2" color="text.secondary">{volunteer.phone || "-"}</Typography>
          </Box>
          <Chip label={volunteer.type || "Adult"} size="small" sx={{
            backgroundColor: volunteer.type === "Teen" ? "#e8f5e9" : "#e3f2fd",
            color: volunteer.type === "Teen" ? "#4caf50" : "#2196f3",
            fontWeight: 600
          }} />
        </Box>

        {/* Application Detail */}
        <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
          Application Detail
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Field label="Name" value={volunteer.name || `${volunteer.firstName || ""} ${volunteer.lastName || ""}`} />
          </Grid>
          <Grid item xs={4}><Field label="Email" value={volunteer.email} /></Grid>
          <Grid item xs={4}><Field label="Phone" value={volunteer.phone} /></Grid>
          <Grid item xs={4}><Field label="Area of Interest" value={volunteer.areaOfInterest || volunteer.interest} /></Grid>
          <Grid item xs={4}><Field label="Availability" value={volunteer.availability} /></Grid>
          <Grid item xs={4}><Field label="Applied On" value={volunteer.appliedOn || volunteer.createdAt?.slice(0, 10)} /></Grid>

          {/* Skills & Expertise chips */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Skills & Expertise</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
              {skillsList.map((skill, idx) => (
                <Chip key={idx} label={skill} size="small" sx={{
                  backgroundColor: SKILL_COLORS[idx % SKILL_COLORS.length] + "20",
                  color: SKILL_COLORS[idx % SKILL_COLORS.length],
                  fontWeight: 600
                }} />
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Approval - Assignment Details */}
        <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
          Approval - Assignment Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Field label="Assigned Grade" value={volunteer.assignedClass || volunteer.assignedGrade} />
          </Grid>
          <Grid item xs={4}>
            <Field label="Assigned Section" value={volunteer.assignedSection} />
          </Grid>
          <Grid item xs={4}>
            <Field label="Role" value={volunteer.assignedRole} />
          </Grid>
          <Grid item xs={4}>
            <Field label="Reviewed On" value={volunteer.reviewedOn || volunteer.updatedAt?.slice(0, 10)} />
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box mt={0.5}>
              <Chip
                label={st === "un-assigned" ? "Un-Assigned" : st.charAt(0).toUpperCase() + st.slice(1)}
                size="small"
                sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600 }}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Activate / Deactivate Toggle */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          p: 2, borderRadius: 2,
          backgroundColor: active ? "#e8f5e9" : "#fce4ec",
          border: `1px solid ${active ? "#a5d6a7" : "#f48fb1"}`
        }}>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" color={active ? "#2e7d32" : "#c62828"}>
              {active ? "Volunteer is Active" : "Volunteer is Deactivated"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Toggle to activate or deactivate this volunteer account.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={handleToggleStatus}
                disabled={toggling}
                color={active ? "success" : "default"}
              />
            }
            label={active ? "Active" : "Inactive"}
            sx={{ ml: 2 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VolunteerDetailDialog;
